import type { RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useScrollPosition } from '../contexts/useScrollPositionContext';

// Type for FlashList ref - using any to avoid dependency on @shopify/flash-list types
interface FlashListRef {
  scrollToOffset: (params: { offset: number; animated: boolean }) => void;
}

interface UseScrollPositionPreservationOptions {
  scrollKey: string;
  scrollRef: RefObject<FlashListRef>;
  isLoading?: boolean;
  hasData?: boolean;
  restoreDelay?: number;
  saveThreshold?: number;
}

interface ScrollPositionPreservationReturn {
  handleScroll: (event: {
    nativeEvent: { contentOffset: { y: number } };
  }) => void;
  hasRestoredScrollRef: React.MutableRefObject<boolean>;
}

/**
 * Custom hook for preserving scroll position across navigation.
 *
 * @param options Configuration options for scroll preservation
 * @returns Object containing scroll handlers and refs
 *
 * @example
 * ```tsx
 * const sectionListRef = useRef(null);
 * const { handleScroll, hasRestoredScrollRef } = useScrollPositionPreservation({
 *   scrollKey: 'community-page-123',
 *   scrollRef: sectionListRef,
 *   isLoading: isLoadingData,
 *   hasData: data.length > 0,
 * });
 *
 * return (
 *   <SectionList
 *     ref={sectionListRef}
 *     onScroll={handleScroll}
 *     scrollEventThrottle={100}
 *     // ... other props
 *   />
 * );
 * ```
 */
export function useScrollPositionPreservation({
  scrollKey,
  scrollRef,
  isLoading = false,
  hasData = true,
  restoreDelay = 100,
  saveThreshold = 100,
}: UseScrollPositionPreservationOptions): ScrollPositionPreservationReturn {
  const { getScrollPosition, setScrollPosition } = useScrollPosition();
  const hasRestoredScrollRef = useRef(false);

  // Handle scroll position restoration
  useEffect(() => {
    if (!hasRestoredScrollRef.current && !isLoading && hasData) {
      const savedPosition = getScrollPosition(scrollKey);
      if (savedPosition && scrollRef.current) {
        // Use a configurable delay to ensure the list is fully rendered
        const timeoutId = setTimeout(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollToOffset({
              offset: savedPosition.offset,
              animated: false,
            });
          }
          hasRestoredScrollRef.current = true;
        }, restoreDelay);

        return () => clearTimeout(timeoutId);
      } else {
        hasRestoredScrollRef.current = true;
      }
    }
  }, [isLoading, hasData, getScrollPosition, scrollKey, restoreDelay]);

  // Handle scroll position saving
  const handleScroll = useCallback(
    (event: { nativeEvent: { contentOffset: { y: number } } }) => {
      const { y } = event.nativeEvent.contentOffset;
      // Only save if we've scrolled more than the threshold to avoid saving minor scrolls
      if (y > saveThreshold) {
        setScrollPosition(scrollKey, y);
      }
    },
    [setScrollPosition, scrollKey, saveThreshold],
  );

  // Reset scroll restoration flag when scroll key changes
  useEffect(() => {
    hasRestoredScrollRef.current = false;
  }, [scrollKey]);

  return {
    handleScroll,
    hasRestoredScrollRef,
  };
}
